import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { articles, categories } from '@/mocks/siteData';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-20 flex flex-col items-center justify-center py-32">
          <div className="w-20 h-20 rounded-full bg-background-100 flex items-center justify-center mb-4">
            <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
          </div>
          <h2 className="font-heading text-xl text-foreground-900 mb-2">文章未找到</h2>
          <p className="text-sm text-foreground-600 mb-6">您访问的文章不存在或已被移除</p>
          <Link
            to="/category"
            className="px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            返回分类浏览
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const category = categories.find((c) => c.id === article.categoryId);
  const relatedArticles = articles
    .filter((a) => a.categoryId === article.categoryId && a.id !== article.id)
    .slice(0, 3);

  const breadcrumbItems = [
    { label: '首页', path: '/' },
    { label: '分类浏览', path: '/category' },
    ...(category ? [{ label: category.name, path: `/category?tab=${category.slug}` }] : []),
    { label: article.title },
  ];

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    'headline': article.title,
    'description': article.excerpt,
    'author': {
      '@type': 'Organization',
      'name': article.author,
    },
    'datePublished': article.date,
    'image': article.imageUrl,
    'publisher': {
      '@type': 'Organization',
      'name': '真人网站精选推荐',
    },
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <script type="application/ld+json">{JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        'itemListElement': breadcrumbItems.map((item, index) => ({
          '@type': 'ListItem',
          'position': index + 1,
          'name': item.label,
          'item': item.path ? `https://recosearch.github.io${item.path}` : undefined,
        })),
      })}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          {/* Article Header */}
          <section className="w-full bg-background-100 pt-20 pb-0">
            <div className="relative w-full h-64 md:h-96 overflow-hidden">
              <img
                src={article.imageUrl}
                alt={article.title}
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background-100 via-background-100/60 to-transparent"></div>
            </div>
          </section>

          {/* Article Content */}
          <article className="w-full bg-background-50 -mt-20 relative z-10">
            <div className="max-w-3xl mx-auto px-4 md:px-6 lg:px-10">
              <div className="bg-background-50 rounded-t-2xl pt-8">
                <div className="mb-4">
                  <Breadcrumb items={breadcrumbItems} />
                </div>

                {category && (
                  <Link
                    to={`/category?tab=${category.slug}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-4 hover:bg-accent-200 transition-colors cursor-pointer"
                  >
                    <i className={`${category.icon} text-[10px]`}></i>
                    {category.name}
                  </Link>
                )}

                <h1 className="font-heading text-xl md:text-3xl text-foreground-900 mb-4 leading-tight">
                  {article.title}
                </h1>

                <div className="flex flex-wrap items-center gap-3 text-sm text-foreground-500 mb-6 pb-6 border-b border-background-200/70">
                  <span>{article.author}</span>
                  <span>·</span>
                  <span>{article.date}</span>
                  <span>·</span>
                  <span>{article.readTime}</span>
                </div>

                <div
                  className="prose-custom text-sm md:text-base text-foreground-800 leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: article.content }}
                />

                {/* Tags */}
                <div className="mt-10 pt-6 border-t border-background-200/70">
                  <div className="flex flex-wrap items-center gap-2">
                    {article.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </article>

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <section className="w-full bg-background-100 py-14 md:py-20">
              <div className="max-w-6xl mx-auto px-4 md:px-6 lg:px-10">
                <h2 className="font-heading text-xl md:text-3xl text-foreground-900 mb-8">
                  相关推荐
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                  {relatedArticles.map((relArticle) => {
                    const relCat = categories.find((c) => c.id === relArticle.categoryId);
                    return (
                      <Link
                        key={relArticle.id}
                        to={`/article/${relArticle.slug}`}
                        className="group bg-background-50 rounded-xl overflow-hidden transition-all duration-300 hover:scale-[1.02] cursor-pointer flex flex-col"
                      >
                        <div className="relative w-full h-44 overflow-hidden">
                          <img
                            src={relArticle.imageUrl}
                            alt={relArticle.title}
                            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                          />
                        </div>
                        <div className="p-4 flex flex-col flex-1">
                          {relCat && (
                            <span className="inline-block px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-700 text-[10px] font-medium mb-2 w-fit">
                              {relCat.name}
                            </span>
                          )}
                          <h3 className="font-heading text-sm text-foreground-900 mb-2 leading-snug group-hover:text-primary-600 transition-colors line-clamp-2 flex-1">
                            {relArticle.title}
                          </h3>
                          <div className="flex items-center gap-3 text-[11px] text-foreground-500">
                            <span>{relArticle.date}</span>
                            <span>·</span>
                            <span>{relArticle.readTime}</span>
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>
            </section>
          )}
        </main>

        <Footer />
      </div>
    </>
  );
}